import { useState, memo } from 'react';
import { Box, Typography, Paper, Button, IconButton, Tooltip, Collapse } from '@mui/material';
import { Delete, OndemandVideo, ExpandMore, ExpandLess, ChevronLeft, ChevronRight, Label } from '@mui/icons-material';
import { BEHAVIORAL_CODES } from './studentLabelingConfig';
import ReplayEventLog from './ReplayEventLog';

// Extracted outside and memoized — prevents re-mount on parent re-render (playback ticks)
const TagItem = memo(({ 
  block, 
  isActive, 
  onBlockClick, 
  onDeleteBlock, 
  onAssignBehavior, 
  formatTime,
  onPause,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerThinking, setPickerThinking] = useState(false)

  const displayBehavior = block.behavior || block.suggestedBehavior
  const isLabeled = block.behavior !== null
  const color = isLabeled ? (displayBehavior?.color || '#6b7280') : '#d1d5db'

  const handleLabelClick = (e) => {
    e.stopPropagation()
    if (pickerOpen) {
      setPickerOpen(false)
      setPickerThinking(false)
    } else {
      if (onPause) onPause()
      setPickerOpen(true)
      setPickerThinking(false)
    }
  }

  const handleSelectBehavior = (e, code, thinkingSubcategory = null) => {
    e.stopPropagation()
    if (onAssignBehavior) {
      onAssignBehavior(block.id, code, thinkingSubcategory)
    }
    setPickerOpen(false)
    setPickerThinking(false)
  }

  return (
    <Box>
      <Box
        onClick={onBlockClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          p: 0.75,
          borderRadius: pickerOpen ? '6px 6px 0 0' : 1.5,
          bgcolor: isActive ? '#f3f4f6' : isLabeled ? `${displayBehavior?.color}10` : '#fff',
          border: isActive 
            ? '2px solid #9ca3af' 
            : isLabeled ? `1px solid ${displayBehavior?.color}30` : '1px dashed #9ca3af',
          borderBottom: pickerOpen ? '1px solid #e5e7eb' : undefined,
          cursor: 'pointer',
          '&:hover': { bgcolor: isLabeled ? `${displayBehavior?.color}20` : '#e5e7eb' }
        }}
      >
        <Box sx={{ 
          width: 8, 
          height: 8, 
          borderRadius: '50%', 
          bgcolor: color,
          flexShrink: 0 
        }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: isLabeled ? displayBehavior?.color : '#6b7280', 
              fontWeight: 600, 
              display: 'block', 
              lineHeight: 1.2,
              fontStyle: isLabeled ? 'normal' : 'italic'
            }}
          >
            {isLabeled ? (
              <>
                {displayBehavior?.label} ({block.segmentNumber})
                {block.thinkingSubcategory && (
                  <span style={{ fontWeight: 400, marginLeft: 4 }}>
                    ({BEHAVIORAL_CODES.find(b => b.id === 'thinking')?.subcategories?.find(s => s.id === block.thinkingSubcategory)?.label})
                  </span>
                )}
              </>
            ) : (
              `Segment (${block.segmentNumber})`
            )}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
            {formatTime(block.startTime)} - {formatTime(block.endTime)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
          <IconButton 
            size="small" 
            onClick={handleLabelClick}
            sx={{ 
              p: 0.25, 
              color: isLabeled ? displayBehavior?.color : '#9ca3af', 
              '&:hover': { color: '#3b82f6', bgcolor: '#eff6ff' } 
            }}
          >
            <Label sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => onDeleteBlock(block.id)}
            sx={{ 
              p: 0.25, 
              color: '#9ca3af', 
              '&:hover': { color: '#ef4444', bgcolor: '#fef2f2' } 
            }}
          >
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Inline label picker */}
      {pickerOpen && onAssignBehavior && (
        <Box sx={{ 
          border: '1px solid #e5e7eb', 
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          bgcolor: '#fff',
          overflow: 'hidden',
        }}>
          {BEHAVIORAL_CODES.map((code) => {
            const isSelected = block.behavior?.id === code.id
            const hasSubcategories = code.subcategories && code.subcategories.length > 0
            
            return (
              <Box key={code.id}>
                <Box
                  onClick={(e) => {
                    if (hasSubcategories) {
                      e.stopPropagation()
                      setPickerThinking(!pickerThinking)
                    } else {
                      handleSelectBehavior(e, code)
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    bgcolor: isSelected ? `${code.color}15` : 'transparent',
                    borderLeft: isSelected ? `3px solid ${code.color}` : '3px solid transparent',
                    '&:hover': { bgcolor: '#f9fafb' },
                  }}
                >
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '3px', 
                    bgcolor: code.color, 
                    flexShrink: 0 
                  }} />
                  <Typography variant="caption" sx={{ 
                    flex: 1, 
                    color: '#374151', 
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '0.7rem',
                  }}>
                    {code.label}
                  </Typography>
                  {hasSubcategories && (
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>▶</Typography>
                  )}
                  {isSelected && !hasSubcategories && (
                    <Typography variant="caption" sx={{ color: code.color, fontSize: '0.7rem' }}>✓</Typography>
                  )}
                </Box>

                {/* Thinking subcategories */}
                {hasSubcategories && pickerThinking && (
                  <Box sx={{ bgcolor: '#f9fafb', borderTop: '1px solid #f3f4f6' }}>
                    {code.subcategories.map(sub => {
                      const isSubSelected = block.thinkingSubcategory === sub.id
                      return (
                        <Box
                          key={sub.id}
                          onClick={(e) => handleSelectBehavior(e, code, sub.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1,
                            py: 0.4,
                            pl: 3,
                            cursor: 'pointer',
                            bgcolor: isSubSelected ? `${code.color}10` : 'transparent',
                            '&:hover': { bgcolor: '#f3f4f6' },
                          }}
                        >
                          <Typography variant="caption" sx={{ 
                            color: '#6b7280', 
                            fontSize: '0.65rem',
                            fontWeight: isSubSelected ? 600 : 400,
                          }}>
                            {sub.label}
                          </Typography>
                          {isSubSelected && (
                            <Typography variant="caption" sx={{ color: code.color, fontSize: '0.65rem' }}>✓</Typography>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
})

export default function StudentLabelingSidebar({
  activeRecording,
  taggedBlocks,
  handleStartRecording,
  handleStopRecording,
  handleDeleteBlock,
  handleBlockClick,
  onAssignBehavior = null,
  formatTime,
  onOpenCodebook,
  showStartButtons = true,
  showTagsSection = true,
  emptyMessage = "No tags yet. Start recording to label what you're doing.",
  events = [],
  startTime = 0,
  onPause,
  currentTime = 0,
}) {
  const [sidebarView, setSidebarView] = useState('labels') // 'labels' or 'events'
  const [codebookExpanded, setCodebookExpanded] = useState(true)
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0)
  const [thinkingExpanded, setThinkingExpanded] = useState(false)

  // Add segment numbers before splitting
  const blocksWithIndex = taggedBlocks.map((block, index) => ({ ...block, segmentNumber: index + 1 }))
  const labeledBlocks = blocksWithIndex.filter(b => b.behavior !== null)
  const unlabeledBlocks = blocksWithIndex.filter(b => b.behavior === null)

  const handlePrevSegment = () => {
    if (taggedBlocks.length === 0) return
    const newIndex = currentSegmentIndex > 0 ? currentSegmentIndex - 1 : taggedBlocks.length - 1
    setCurrentSegmentIndex(newIndex)
    handleBlockClick(taggedBlocks[newIndex])
  }

  const handleNextSegment = () => {
    if (taggedBlocks.length === 0) return
    const newIndex = currentSegmentIndex < taggedBlocks.length - 1 ? currentSegmentIndex + 1 : 0
    setCurrentSegmentIndex(newIndex)
    handleBlockClick(taggedBlocks[newIndex])
  }

  const renderTagItem = (block) => (
    <TagItem 
      key={block.id} 
      block={block}
      isActive={taggedBlocks[currentSegmentIndex]?.id === block.id}
      onBlockClick={() => {
        handleBlockClick(block);
        setCurrentSegmentIndex(taggedBlocks.findIndex(b => b.id === block.id));
      }}
      onDeleteBlock={handleDeleteBlock}
      onAssignBehavior={onAssignBehavior}
      formatTime={formatTime}
      onPause={onPause}
    />
  )

  return (
    <Box sx={{ 
      bgcolor: '#ffffff', 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      overflow: 'hidden' 
    }}>
      {/* Header with toggle */}
      <Box sx={{ 
        p: 1.5, 
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#111827' }}>
          {sidebarView === 'labels' ? 'Label Your Session' : 'Event Log'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            onClick={() => setSidebarView(sidebarView === 'labels' ? 'events' : 'labels')}
            sx={{ fontSize: '0.65rem', textTransform: 'none', color: '#3b82f6', minWidth: 0, px: 1 }}
          >
            {sidebarView === 'labels' ? 'Events' : 'Labels'}
          </Button>
          <Tooltip title="Watch Tutorial">
            <IconButton size="small" onClick={onOpenCodebook} sx={{ color: '#3b82f6' }}>
              <OndemandVideo sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Event Log View ──────────────────────────────────────────── */}
      {sidebarView === 'events' && (
        <ReplayEventLog events={events} currentTime={currentTime} startTime={startTime} />
      )}

      {/* ── Labels View (original content) ──────────────────────────── */}
      {sidebarView === 'labels' && (
        <>
          {/* Main Content - Scrollable */}
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {/* Codebook Section - Collapsible */}
            <Box sx={{ borderBottom: '1px solid #e5e7eb' }}>
              <Box 
                onClick={() => setCodebookExpanded(!codebookExpanded)}
                sx={{ 
                  p: 1.5, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f9fafb' }
                }}
              >
                <Typography 
                  variant="caption" 
                  fontWeight="700" 
                  sx={{ 
                    color: '#6b7280', 
                    textTransform: 'uppercase', 
                    letterSpacing: 0.5
                  }}
                >
                  Codebook
                </Typography>
                {codebookExpanded ? (
                  <ExpandLess sx={{ fontSize: 18, color: '#9ca3af' }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 18, color: '#9ca3af' }} />
                )}
              </Box>
              
              <Collapse in={codebookExpanded}>
                <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {BEHAVIORAL_CODES.map((code) => {
                    const isRecording = activeRecording?.behavior.id === code.id;
                    return (
                      <Paper 
                        key={code.id}
                        elevation={0} 
                        sx={{ 
                          border: isRecording ? `2px solid ${code.color}` : '1px solid #e5e7eb', 
                          borderRadius: 1.5, 
                          p: 1, 
                          transition: '0.2s',
                          bgcolor: isRecording ? `${code.color}10` : 'transparent',
                          '&:hover': { borderColor: code.color, bgcolor: '#f9fafb' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: code.color, 
                              ...(isRecording && { animation: 'pulse 1s infinite' }) 
                            }} />
                            <Typography variant="body2" fontWeight="500" sx={{ color: '#111827' }}>
                              {code.label}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {code.subcategories && code.subcategories.length > 0 && (
                              <IconButton 
                                size="small" 
                                onClick={(e) => { e.stopPropagation(); setThinkingExpanded(!thinkingExpanded) }}
                                sx={{ p: 0.25, color: '#9ca3af' }}
                              >
                                {thinkingExpanded ? (
                                  <ExpandLess sx={{ fontSize: 16 }} />
                                ) : (
                                  <ExpandMore sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            )}
                            {showStartButtons && (
                              isRecording ? (
                                <Button 
                                  size="small" 
                                  variant="contained" 
                                  onClick={handleStopRecording} 
                                  sx={{ 
                                    bgcolor: '#dc2626', 
                                    fontSize: '0.65rem', 
                                    py: 0.25, 
                                    px: 1.5, 
                                    minWidth: 0,
                                    '&:hover': { bgcolor: '#b91c1c' }
                                  }}
                                >
                                  Stop
                                </Button>
                              ) : (
                                <Button 
                                  size="small" 
                                  variant="outlined" 
                                  onClick={() => handleStartRecording(code)} 
                                  disabled={!!activeRecording} 
                                  sx={{ 
                                    borderColor: code.color, 
                                    color: code.color, 
                                    fontSize: '0.65rem', 
                                    py: 0.25, 
                                    px: 1.5, 
                                    minWidth: 0,
                                    '&:hover': { 
                                      borderColor: code.color, 
                                      bgcolor: `${code.color}10` 
                                    },
                                    '&:disabled': {
                                      borderColor: '#e5e7eb',
                                      color: '#9ca3af'
                                    }
                                  }}
                                >
                                  Start
                                </Button>
                              )
                            )}
                          </Box>
                        </Box>
                        {code.description && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#6b7280', 
                              display: 'block', 
                              ml: 2.5, 
                              mt: 0.5,
                              lineHeight: 1.3
                            }}
                          >
                            {code.description}
                          </Typography>
                        )}
                        {/* Thinking subcategories dropdown */}
                        {code.subcategories && code.subcategories.length > 0 && (
                          <Collapse in={thinkingExpanded}>
                            <Box sx={{ ml: 2.5, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {code.subcategories.map(sub => (
                                <Typography 
                                  key={sub.id} 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#6b7280', 
                                    fontSize: '0.7rem',
                                    pl: 1,
                                    borderLeft: `2px solid ${code.color}30`,
                                    lineHeight: 1.6,
                                  }}
                                >
                                  {sub.label}
                                </Typography>
                              ))}
                            </Box>
                          </Collapse>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>

            {showTagsSection && (
              /* Tagged Blocks Section */
              <Box sx={{ bgcolor: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
                <Box 
                  onClick={() => setTagsExpanded(!tagsExpanded)}
                  sx={{ 
                    p: 1.5, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f3f4f6' }
                  }}
                >
                  <Typography 
                    variant="caption" 
                    fontWeight="700" 
                    sx={{ 
                      color: '#6b7280', 
                      textTransform: 'uppercase', 
                      letterSpacing: 0.5
                    }}
                  >
                    Segments ({taggedBlocks.length})
                  </Typography>
                  {tagsExpanded ? (
                    <ExpandLess sx={{ fontSize: 18, color: '#9ca3af' }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 18, color: '#9ca3af' }} />
                  )}
                </Box>
                
                <Collapse in={tagsExpanded}>
                  <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column' }}>
                    {taggedBlocks.length === 0 ? (
                      <Box sx={{ 
                        p: 2, 
                        textAlign: 'center', 
                        color: '#9ca3af', 
                        border: '2px dashed #e5e7eb', 
                        borderRadius: 2,
                        bgcolor: '#fff'
                      }}>
                        <Typography variant="caption">
                          {emptyMessage}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1.5, 
                      }}>
                        {/* Unlabeled Section */}
                        {unlabeledBlocks.length > 0 && (
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#9ca3af', 
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                mb: 0.5,
                                display: 'block'
                              }}
                            >
                              Unlabeled ({unlabeledBlocks.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1 }}>
                              {unlabeledBlocks.map(renderTagItem)}
                            </Box>
                          </Box>
                        )}

                        {/* Labeled Section */}
                        {labeledBlocks.length > 0 && (
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#9ca3af', 
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                mb: 0.5,
                                display: 'block'
                              }}
                            >
                              Labeled ({labeledBlocks.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 1 }}>
                              {labeledBlocks.map(renderTagItem)}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>

          {/* Segment Navigation */}
          {taggedBlocks.length > 0 && (
            <Box sx={{ 
              px: 1.5, 
              py: 1, 
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#ffffff'
            }}>
              <IconButton 
                size="small" 
                onClick={handlePrevSegment}
                sx={{ 
                  color: '#374151',
                  '&:hover': { bgcolor: '#e5e7eb' }
                }}
              >
                <ChevronLeft sx={{ fontSize: 20 }} />
              </IconButton>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" fontWeight="600" sx={{ color: '#374151', display: 'block' }}>
                  Segment {currentSegmentIndex + 1} of {taggedBlocks.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                  {formatTime(taggedBlocks[currentSegmentIndex]?.startTime || 0)} - {formatTime(taggedBlocks[currentSegmentIndex]?.endTime || 0)}
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={handleNextSegment}
                sx={{ 
                  color: '#374151',
                  '&:hover': { bgcolor: '#e5e7eb' }
                }}
              >
                <ChevronRight sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}
        </>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Box>
  );
}